import { DOMParser } from '@tiptap/pm/model';
import type { Transaction } from '@tiptap/pm/state';
import type { EditorView } from '@tiptap/pm/view';
import {
  markdownToHtml,
  looksLikeMarkdown,
  prefersClipboardHtmlAsRichContent
} from '../utils/serialization';

export type OversizedMarkdownPasteHandler = (
  view: EditorView,
  event: ClipboardEvent,
  nextMarkdown: string
) => boolean;

function buildMarkdownPasteTransaction(view: EditorView, plain: string): Transaction {
  const html = markdownToHtml(plain);
  const wrap = document.createElement('div');
  wrap.innerHTML = html;
  const slice = DOMParser.fromSchema(view.state.schema).parseSlice(wrap);
  return view.state.tr.replaceSelection(slice);
}

export function createMarkdownPasteHandler(
  showMarkdownTextareaRef: { current: boolean },
  options?: {
    getNextMarkdown: (view: EditorView, tr: Transaction) => string;
    /** Return true when the oversized paste was handled (warning shown). */
    onOversizedPaste: OversizedMarkdownPasteHandler;
    characterLimit: number;
  }
) {
  return (view: EditorView, event: Event): boolean => {
    if (showMarkdownTextareaRef.current) return false;
    const ce = event as ClipboardEvent;
    const plain = ce.clipboardData?.getData('text/plain') ?? '';
    if (!plain.trim() || !looksLikeMarkdown(plain)) return false;
    const htmlRaw = ce.clipboardData?.getData('text/html') ?? '';
    if (prefersClipboardHtmlAsRichContent(htmlRaw, plain)) return false;
    ce.preventDefault();
    try {
      const tr = buildMarkdownPasteTransaction(view, plain);
      if (options) {
        const nextMarkdown = options.getNextMarkdown(view, tr);
        if (nextMarkdown.length > options.characterLimit) {
          if (options.onOversizedPaste(view, ce, nextMarkdown)) {
            return true;
          }
        }
      }
      view.dispatch(tr);
    } catch {
      return false;
    }
    return true;
  };
}
